import React from 'react';
import { useSiteImages } from '../context/SiteImagesContext';
import { ArrowRight, FileText, Scale, ShieldCheck } from 'lucide-react';
import { PageHeaderBanner } from '../components/PageHeaderBanner';

type LegalPageType = 'privacy-policy' | 'terms' | 'disclaimer';

interface LegalPageProps {
  type: LegalPageType;
  onNavigate: (page: string) => void;
}

interface LegalSection {
  title: string;
  paragraphs: string[];
  bullets?: string[];
}

interface LegalDocument {
  title: string;
  shortTitle: string;
  description: string;
  badge: string;
  icon: React.ReactNode;
  sections: LegalSection[];
}

const lastUpdated = '16 October 2026';

const documents: Record<LegalPageType, LegalDocument> = {
  'privacy-policy': {
    title: 'Privacy Policy',
    shortTitle: 'Privacy',
    description:
      'How Saints Tech Solutions collects, uses, and safeguards information when you browse our website or contact us about our equipment and services.',
    badge: 'Your information, handled with care',
    icon: <ShieldCheck className="w-3.5 h-3.5" />,
    sections: [
      {
        title: 'Information you choose to provide',
        paragraphs: [
          'When you send an inquiry, request a quotation, or ask for service, we may receive the details you enter, such as your name, email address, telephone number, county or location, product interests, message, and the items included in a quotation request. Please provide only information relevant to your request.'
        ]
      },
      {
        title: 'Information collected when you use the site',
        paragraphs: [
          'Our website and service providers may process basic technical and usage information, such as pages visited, browser and device details, approximate location derived from network information, and the time and source of a visit. We use Google Analytics to understand website usage. Analytics technologies, including cookies or similar identifiers, may be used for this purpose.'
        ],
        bullets: [
          'Your browser settings can be used to limit or remove cookies, though some site features may behave differently.',
          'Google provides information about its privacy practices and controls through its own services.'
        ]
      },
      {
        title: 'How we use information',
        paragraphs: [
          'We use information to respond to questions, prepare quotations, arrange product or technical support discussions, operate and secure the website, understand how visitors use our pages, and meet applicable legal obligations. We may contact you about the specific request you submitted.'
        ]
      },
      {
        title: 'When information may be shared',
        paragraphs: [
          'We may share relevant information with service providers who help us host and operate the website, store or manage inquiries, deliver email, or measure website usage. They may process information only as needed to provide their services. We may also disclose information where required by law or where reasonably necessary to protect the rights, safety, and security of our business, users, or the public.'
        ]
      },
      {
        title: 'Storage, security, and international processing',
        paragraphs: [
          'We retain information for as long as reasonably needed to handle your request, maintain relevant business records, and meet legal requirements. We use reasonable safeguards designed to protect information, but no internet transmission or storage system can be guaranteed completely secure. Some service providers may process information outside Kenya, subject to the safeguards applicable to that processing.'
        ]
      },
      {
        title: 'Your choices and privacy rights',
        paragraphs: [
          'Depending on the circumstances and applicable law, you may have rights to request access to, correction of, deletion of, or objection to certain processing of your personal data. You may also ask us about how your information is used. We will handle requests in accordance with applicable Kenyan data protection law.'
        ]
      },
      {
        title: 'Children and third-party websites',
        paragraphs: [
          'This website is intended for business and general audiences and is not designed to collect personal information from children. Our pages may link to third-party websites; their privacy practices are governed by their own policies, not this one.'
        ]
      },
      {
        title: 'Updates and contact',
        paragraphs: [
          'We may update this policy as our website or practices change. The date above indicates when it was most recently reviewed. For a privacy question or request, contact us at info@saintstech.co.ke or write to Saints Tech Solutions, Juja Road 478/B Green Mall, Nairobi, Kenya.'
        ]
      }
    ]
  },
  terms: {
    title: 'Terms of Use',
    shortTitle: 'Terms',
    description:
      'The terms that apply when you access and use the Saints Tech Solutions website, product information, and online inquiry tools.',
    badge: 'Clear terms for using this website',
    icon: <FileText className="w-3.5 h-3.5" />,
    sections: [
      {
        title: 'Using this website',
        paragraphs: [
          'By using this website, you agree to use it lawfully and in a way that does not interfere with its operation or other visitors’ use. If you do not agree with these terms, please do not use the website.'
        ],
        bullets: [
          'Do not attempt to gain unauthorized access to the website, its systems, or another person’s information.',
          'Do not misuse inquiry forms, submit deceptive information, or upload harmful code.',
          'You are responsible for ensuring that information you submit is accurate and that you are entitled to provide it.'
        ]
      },
      {
        title: 'Product information and quotations',
        paragraphs: [
          'Website content is provided for general product and service information. Specifications, images, configurations, prices, availability, delivery timelines, and service coverage may change and should be confirmed with us for your requirements.',
          'Submitting an inquiry or requesting a quotation does not create a sales contract or guarantee availability. A purchase or service engagement is subject to a separate written quotation, order confirmation, or agreement accepted by both parties.'
        ]
      },
      {
        title: 'Intellectual property',
        paragraphs: [
          'Unless otherwise stated, the website’s text, branding, graphics, and other materials belong to Saints Tech Solutions or are used with permission. You may view and print pages for personal or internal business reference. You must not reproduce, modify, distribute, or commercially exploit website content without prior written permission, except where applicable law permits.'
        ]
      },
      {
        title: 'Third-party services and links',
        paragraphs: [
          'The website may link to third-party websites or services for convenience. We do not control those services and are not responsible for their content, availability, or terms. Your use of a third-party service is subject to its own terms and policies.'
        ]
      },
      {
        title: 'Website availability and liability',
        paragraphs: [
          'We take reasonable steps to keep the website useful and available, but do not guarantee uninterrupted access, error-free content, or that every page is always current. To the extent permitted by law, Saints Tech Solutions is not liable for indirect or consequential loss arising from use of, or inability to use, this website. Nothing in these terms limits a right or liability that cannot lawfully be limited.'
        ]
      },
      {
        title: 'Separate sales and service agreements',
        paragraphs: [
          'These website terms do not replace any written purchase, installation, service, or warranty terms agreed for a specific order. The terms in the applicable written agreement govern that transaction to the extent of any inconsistency.'
        ]
      },
      {
        title: 'Applicable law and changes',
        paragraphs: [
          'These terms are governed by the laws of Kenya, subject to any mandatory legal protections that apply to you. We may update these terms when our website or services change; the date above shows the latest review date. Continued use after an update means you accept the revised terms to the extent permitted by law.'
        ]
      },
      {
        title: 'Contact us',
        paragraphs: [
          'Questions about these terms can be sent to info@saintstech.co.ke or addressed to Saints Tech Solutions, Juja Road 478/B Green Mall, Nairobi, Kenya.'
        ]
      }
    ]
  },
  disclaimer: {
    title: 'Disclaimer',
    shortTitle: 'Disclaimer',
    description:
      'Important context about website information, equipment suitability, water quality, estimates, and the limits of general online guidance.',
    badge: 'Please review before relying on site information',
    icon: <Scale className="w-3.5 h-3.5" />,
    sections: [
      {
        title: 'General information only',
        paragraphs: [
          'Information on this website is provided for general information and discussion. It is not a substitute for a site assessment, professional engineering advice, laboratory testing, financial advice, or advice from a qualified legal or regulatory professional. Do not rely on a website description alone to make a safety-critical or compliance decision.'
        ]
      },
      {
        title: 'Equipment selection and installation',
        paragraphs: [
          'Equipment performance and suitability depend on factors such as source-water characteristics, required output, site conditions, utilities, installation, operation, and maintenance. Any configuration discussed online is preliminary until your requirements and site conditions have been reviewed and confirmed in writing.'
        ]
      },
      {
        title: 'Water quality and regulatory requirements',
        paragraphs: [
          'A treatment system does not by itself guarantee that water will meet a particular drinking-water standard or be safe for every use. Water should be tested by a qualified laboratory, and systems should be designed, operated, cleaned, and maintained for the specific source water and intended application. Customers are responsible for confirming applicable permits, testing, approvals, and regulatory requirements with the relevant authorities.'
        ]
      },
      {
        title: 'Materials, standards, and certifications',
        paragraphs: [
          'Descriptions of materials or design features, including stainless-steel grades or food-contact construction, are not a representation that every product or installation holds a particular certification or regulatory approval. Confirm the exact materials, supporting documentation, standards, and approvals applicable to a specific item in its written quotation or technical documents.'
        ]
      },
      {
        title: 'Estimates and illustrative calculations',
        paragraphs: [
          'Any calculator, sample return, capacity, consumption, or business estimate on this website is illustrative only. Results depend on the assumptions and inputs used and are not a promise of revenue, savings, production, payback, or business success. Verify assumptions independently before making a purchase or investment decision.'
        ]
      },
      {
        title: 'Warranty, safety, and maintenance',
        paragraphs: [
          'Warranty coverage, if any, is defined by the written warranty or sales documents provided for the specific purchase. Follow the applicable operating instructions, installation requirements, maintenance schedules, and safety procedures. Do not operate equipment outside its documented conditions or use it for an application for which it has not been assessed.'
        ]
      },
      {
        title: 'Accuracy, external links, and contact',
        paragraphs: [
          'We aim to keep website content useful, but product details and other information may be incomplete, change over time, or contain errors. Third-party links are provided for convenience and do not imply endorsement. For information about a specific product, installation, or claim, contact us at info@saintstech.co.ke before relying on it.'
        ]
      }
    ]
  }
};

export const LegalPage: React.FC<LegalPageProps> = ({ type, onNavigate }) => {
  const document = documents[type];
  const { images } = useSiteImages();

  return (
    <div className="bg-slate-50 min-h-screen">
      <PageHeaderBanner
        breadcrumb={document.shortTitle}
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={document.icon}
        badgeText={document.badge}
        badgeColor="sky"
        title={document.title}
        description={document.description}
        backgroundImage={images.legalBanner}
        gradientTheme="sky-slate"
        onNavigate={onNavigate}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
          <aside className="lg:sticky lg:top-24 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-sm font-bold text-slate-900">On this page</h2>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2.5 py-1">
                {document.sections.length} topics
              </span>
            </div>
            <nav aria-label={`${document.title} contents`}>
              <ol className="space-y-1">
                {document.sections.map((section, index) => (
                  <li key={section.title}>
                    <a
                      href={`#section-${index + 1}`}
                      className="flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-xs leading-relaxed text-slate-600 hover:bg-sky-50 hover:text-sky-800 transition-colors"
                    >
                      <span className="font-semibold text-slate-400 tabular-nums">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-[11px] text-slate-500">Last updated</p>
              <p className="text-xs font-semibold text-slate-700 mt-0.5">{lastUpdated}</p>
            </div>
          </aside>

          <div className="space-y-5">
            <div className="rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 shrink-0 rounded-xl bg-white border border-sky-100 text-sky-700 flex items-center justify-center shadow-sm">
                  {document.icon}
                </span>
                <div>
                  <p className="text-sm font-bold text-slate-900">A clear guide to our website practices</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">
                    Please read the sections relevant to you. If you need clarification about a product or how we handle an inquiry, our team can help.
                  </p>
                </div>
              </div>
            </div>

            {document.sections.map((section, index) => (
              <section
                key={section.title}
                id={`section-${index + 1}`}
                className="scroll-mt-28 rounded-2xl border border-slate-200 bg-white p-5 sm:p-7 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <span className="w-9 h-9 shrink-0 rounded-xl bg-slate-900 text-sky-300 flex items-center justify-center text-xs font-bold tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
                      {section.title}
                    </h2>
                    <div className="mt-3 space-y-3 text-sm leading-7 text-slate-600">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                      {section.bullets && (
                        <ul className="space-y-2 pl-5 list-disc marker:text-sky-600">
                          {section.bullets.map((bullet) => (
                            <li key={bullet}>{bullet}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ))}

            <div className="rounded-2xl bg-slate-950 text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-sky-300">Need help?</p>
                <h2 className="mt-1 text-lg font-bold">Talk to the Saints Tech team</h2>
                <p className="mt-1 text-sm text-slate-300">
                  Contact us about this page or your equipment inquiry.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  onNavigate('contact');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-400"
              >
                Contact us
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
